import React, { createContext, useState, useEffect, useContext } from 'react';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';

const InstallContext = createContext(null);

export function useInstall() {
  return useContext(InstallContext);
}

export function InstallProvider({ children }) {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [canInstall, setCanInstall] = useState(false);
  const [isInstalled, setIsInstalled] = useState(false);
  const [showManualInstructions, setShowManualInstructions] = useState(false);
  const [platform, setPlatform] = useState('unknown');
  const [installing, setInstalling] = useState(false);

  useEffect(() => {
    // Detect platform
    const userAgent = navigator.userAgent || navigator.vendor || window.opera;
    
    if (/iPad|iPhone|iPod/.test(userAgent) && !window.MSStream) {
      setPlatform('ios');
    } else if (/android/i.test(userAgent)) {
      setPlatform('android');
    } else if (/windows/i.test(userAgent)) {
      setPlatform('windows');
    } else if (/macintosh|mac os x/i.test(userAgent)) {
      setPlatform('mac');
    } else {
      setPlatform('other');
    }

    // Check if already installed as PWA
    const checkInstalled = () => {
      const isStandalone = window.matchMedia('(display-mode: standalone)').matches || 
                          window.navigator.standalone || 
                          document.referrer.includes('android-app://');
      setIsInstalled(isStandalone);
    };
    
    checkInstalled();

    // Listen for the beforeinstallprompt event
    const handleBeforeInstallPrompt = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setCanInstall(true);
    };

    // Listen for successful installation
    const handleAppInstalled = () => {
      setIsInstalled(true);
      setCanInstall(false);
      setDeferredPrompt(null);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    window.addEventListener('appinstalled', handleAppInstalled);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
      window.removeEventListener('appinstalled', handleAppInstalled);
    };
  }, []);

  const triggerInstall = async () => {
    if (isInstalled) {
      return { success: true, message: 'Already installed' };
    }

    // If we have the deferred prompt, use it (Chrome, Edge, Samsung Internet, etc.)
    if (deferredPrompt) {
      setInstalling(true);
      try {
        deferredPrompt.prompt();
        const { outcome } = await deferredPrompt.userChoice;
        setInstalling(false);
        
        if (outcome === 'accepted') {
          setDeferredPrompt(null);
          setCanInstall(false);
          return { success: true, message: 'Installation started' };
        } else {
          return { success: false, message: 'Installation cancelled' };
        }
      } catch (error) {
        setInstalling(false);
        return { success: false, message: 'Installation failed' };
      }
    }

    // For browsers that don't support beforeinstallprompt (iOS, Firefox, etc.)
    // Show manual instructions
    setShowManualInstructions(true);
    return { success: false, message: 'Manual installation required' };
  };

  const getInstructions = () => {
    switch (platform) {
      case 'ios':
        return {
          title: 'Add to Home Screen (iPhone/iPad)',
          icon: '🍎',
          steps: [
            { num: 1, text: 'Tap the Share button', icon: '□↑', detail: 'at the bottom of Safari' },
            { num: 2, text: 'Scroll down and tap', icon: '➕', detail: '"Add to Home Screen"' },
            { num: 3, text: 'Tap "Add"', icon: '✓', detail: 'in the top right' }
          ],
          note: 'Must use Safari browser on iOS'
        };
      case 'android':
        return {
          title: 'Add to Home Screen (Android)',
          icon: '🤖',
          steps: [
            { num: 1, text: 'Tap menu button', icon: '⋮', detail: 'in top right corner' },
            { num: 2, text: 'Tap "Install App"', icon: '📲', detail: 'or "Add to Home Screen"' },
            { num: 3, text: 'Tap "Install"', icon: '✓', detail: 'to confirm' }
          ],
          note: 'Works best with Chrome browser'
        };
      case 'windows':
        return {
          title: 'Install App (Windows)',
          icon: '💻',
          steps: [
            { num: 1, text: 'Click install icon', icon: '⊕', detail: 'in address bar (or menu ⋮)' },
            { num: 2, text: 'Click "Install"', icon: '📲', detail: 'in the popup' },
            { num: 3, text: 'App opens', icon: '✓', detail: 'and appears in Start Menu' }
          ],
          note: 'Use Chrome or Edge browser'
        };
      case 'mac':
        return {
          title: 'Add to Dock (Mac)',
          icon: '🖥️',
          steps: [
            { num: 1, text: 'In Chrome: Menu ⋮', icon: '⋮', detail: '→ "Install Excel Era..."' },
            { num: 2, text: 'Or Safari: File', icon: '📁', detail: '→ "Add to Dock"' },
            { num: 3, text: 'App appears', icon: '✓', detail: 'in your Dock' }
          ],
          note: 'Safari or Chrome recommended'
        };
      default:
        return {
          title: 'Add to Home Screen',
          icon: '🌐',
          steps: [
            { num: 1, text: 'Open browser menu', icon: '☰', detail: '(three dots or lines)' },
            { num: 2, text: 'Find "Install"', icon: '📲', detail: 'or "Add to Home Screen"' },
            { num: 3, text: 'Confirm', icon: '✓', detail: 'to add shortcut' }
          ],
          note: 'Steps may vary by browser'
        };
    }
  };

  const value = {
    canInstall: canInstall || !isInstalled, // Show button if can install OR not yet installed
    isInstalled,
    installing,
    triggerInstall,
    platform
  };

  const instructions = getInstructions();

  return (
    <InstallContext.Provider value={value}>
      {children}
      
      {/* Manual Instructions Dialog */}
      <Dialog open={showManualInstructions} onOpenChange={setShowManualInstructions}>
        <DialogContent className="bg-white max-w-sm mx-4 p-0 overflow-hidden">
          <div className="bg-gradient-to-r from-primary to-primary/80 text-white p-5 text-center">
            <div className="text-4xl mb-2">{instructions.icon}</div>
            <h2 className="text-lg font-heading font-bold">{instructions.title}</h2>
          </div>
          
          <div className="p-5 space-y-4">
            <div className="space-y-3">
              {instructions.steps.map((step) => (
                <div key={step.num} className="flex items-start gap-3">
                  <div className="flex-shrink-0 w-7 h-7 bg-primary text-white rounded-full flex items-center justify-center text-sm font-bold">
                    {step.num}
                  </div>
                  <div className="flex-1">
                    <span className="font-semibold text-foreground">{step.text}</span>
                    <span className="text-primary font-bold mx-1">{step.icon}</span>
                    <span className="text-muted-foreground text-sm">{step.detail}</span>
                  </div>
                </div>
              ))}
            </div>
            
            {instructions.note && (
              <p className="text-xs text-amber-600 bg-amber-50 p-2 rounded text-center">
                💡 {instructions.note}
              </p>
            )}
            
            <Button
              onClick={() => setShowManualInstructions(false)}
              className="w-full h-11 bg-primary hover:bg-primary/90 font-bold"
            >
              Got It!
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </InstallContext.Provider>
  );
}

// Reusable Install Button Component
export function InstallButton({ className = '', variant = 'default', size = 'default' }) {
  const { canInstall, isInstalled, installing, triggerInstall } = useInstall();

  if (isInstalled) {
    return null; // Don't show button if already installed
  }

  const handleClick = async () => {
    await triggerInstall();
  };

  const baseStyles = "font-bold flex items-center gap-2";
  
  const variants = {
    default: "bg-green-600 hover:bg-green-700 text-white",
    outline: "border-2 border-green-600 text-green-600 hover:bg-green-50",
    header: "bg-white/20 hover:bg-white/30 text-white border border-white/50",
    landing: "bg-secondary hover:bg-secondary/90 text-white"
  };

  const sizes = {
    default: "px-4 py-2 text-sm",
    sm: "px-3 py-1.5 text-xs",
    lg: "px-6 py-3 text-base"
  };

  return (
    <button
      onClick={handleClick}
      disabled={installing}
      className={`${baseStyles} ${variants[variant] || variants.default} ${sizes[size] || sizes.default} rounded-lg transition-all ${className}`}
    >
      {installing ? (
        <>
          <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none"/>
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"/>
          </svg>
          <span>Installing...</span>
        </>
      ) : (
        <>
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
          </svg>
          <span>Create Shortcut</span>
        </>
      )}
    </button>
  );
}
